import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, MessageCircle, Sparkles, Twitter } from "lucide-react";
import { toast } from "sonner";
import { getSessionToken, getWaitlistStatus } from "@/lib/afritask";
import { formatApiError } from "@/lib/errors";

export default function Waitlist() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!getSessionToken()) {
      navigate("/auth/enter-phone", { replace: true });
      return;
    }
    (async () => {
      try {
        const s = await getWaitlistStatus();
        if (!s.waitlisted) {
          // Already admitted — send them to dashboard
          navigate("/app/dashboard", { replace: true });
          return;
        }
        setStatus(s);
      } catch (e) {
        toast.error(formatApiError(e, "Couldn't load your waitlist status"));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDF9F1] text-neutral-500">
        Loading…
      </div>
    );
  }
  if (!status) return null;

  const pct = Math.round(Math.min(1, status.progress_pct || 0) * 100);
  const boost = status.referral_boost_spots || 0;
  const share = status.referral?.share_urls || {};
  const code = status.referral?.code || "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(share.copy || "");
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF9F1] px-4 py-10 text-neutral-900 sm:py-16" data-testid="waitlist-page">
      <div className="mx-auto max-w-xl space-y-8">
        {/* Hero */}
        <div className="space-y-3 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium text-[#B45309] ring-1 ring-[#B45309]/20">
            <Sparkles className="h-3 w-3" />
            Early access — invite-only
          </div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            You&apos;re on the AfriTask waitlist
          </h1>
        </div>

        {/* Position card */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm sm:p-8">
          <p className="text-xs uppercase tracking-widest text-neutral-500">Your position</p>
          <p
            className="mt-2 text-6xl font-bold tabular-nums text-[#B45309] sm:text-7xl"
            data-testid="waitlist-position"
          >
            #{status.position}
          </p>
          {boost > 0 && (
            <div
              className="mt-4 inline-flex items-center gap-1 rounded-full bg-[#FDF3E4] px-3 py-1 text-xs font-medium text-[#B45309] ring-1 ring-[#B45309]/20"
              data-testid="waitlist-boost-pill"
            >
              You&apos;ve moved up {boost} spot{boost === 1 ? "" : "s"} thanks to your referrals
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-5" data-testid="waitlist-progress">
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-medium text-neutral-800">
              <span className="tabular-nums text-neutral-900">{status.total_waitlisted}</span>
              <span className="text-neutral-500"> / {status.launch_goal} signed up</span>
            </p>
            <p className="text-xs tabular-nums text-neutral-500">{pct}%</p>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full bg-amber-500 transition-[width] duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Referral share */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6" data-testid="waitlist-share">
          <div className="space-y-1">
            <h2 className="text-base font-semibold">Share your code — bring friends, move up the queue</h2>
            <p className="text-sm text-neutral-500">
              Every waitlisted signup with your code bumps you up.
              {status.referral?.referred_waitlisted_count > 0 && (
                <>
                  {" "}
                  <span className="font-medium text-neutral-700">
                    {status.referral.referred_waitlisted_count} signed up so far.
                  </span>
                </>
              )}
            </p>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#FDF3E4] p-3 ring-1 ring-[#B45309]/15">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-neutral-500">Your code</p>
              <p
                className="mt-0.5 font-mono text-lg font-semibold tracking-widest text-[#B45309] sm:text-xl"
                data-testid="waitlist-referral-code"
              >
                {code || "—"}
              </p>
            </div>
            <button
              type="button"
              onClick={copyCode}
              className="rounded-md border border-[#B45309]/30 bg-white px-2.5 py-1.5 text-xs font-medium text-[#B45309] hover:bg-[#FDF3E4]"
              data-testid="waitlist-copy-code-btn"
            >
              Copy
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <a
              href={share.whatsapp || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50"
              data-testid="waitlist-share-whatsapp"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a
              href={share.x || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50"
              data-testid="waitlist-share-x"
            >
              <Twitter className="h-4 w-4" /> X
            </a>
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50"
              data-testid="waitlist-share-copy"
            >
              <Copy className="h-4 w-4" /> Copy link
            </button>
          </div>
        </div>

        <p className="text-center text-sm text-neutral-500">
          We&apos;ll notify you the moment we launch.
        </p>
      </div>
    </div>
  );
}
